import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { productsData } from "../data/productsData";
import SEO from "../components/SEO";
import {
  generateProductSchema,
  generateBreadcrumbSchema,
} from "../utils/schemaMarkup";

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Find product by slug
  const product = productsData.find((p) => {
    const productSlug = p.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return productSlug === slug;
  });

  if (!product) {
    return (
      <div className="page active">
        <h2>Product Not Found</h2>
        <p>The product you're looking for doesn't exist.</p>
        <Link to="/products">Back to Products</Link>
      </div>
    );
  }

  const productSlug = product.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const productUrl = `https://www.ceekayenterprise.com/products/${productSlug}`;

  // Generate SEO-friendly title and description
  const productTitle = `Buy ${product.name} Online | Industrial Grade | Ceekay Enterprise`;
  const productDescription = `${product.name} - ${product.description} Available for bulk orders. High purity industrial grade. Fast delivery across India. Request quote today.`;

  // Extract CAS number and formula if available in keywords
  const casNumber = product.keywords?.find((k) =>
    /^\d+-\d+-\d+$/.test(k)
  );
  const formula = product.keywords?.find((k) =>
    /[A-Z][a-z]?\d*/.test(k) && k.length > 2 && k.length < 10
  );

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://www.ceekayenterprise.com/" },
    { name: "Products", url: "https://www.ceekayenterprise.com/products" },
    { name: product.name, url: productUrl },
  ]);

  const productSchema = generateProductSchema(product);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [productSchema, breadcrumbSchema],
  };

  // Generate keywords from product data
  const seoKeywords = [
    ...product.keywords,
    `buy ${product.name.toLowerCase()} India`,
    `${product.name.toLowerCase()} supplier`,
    `${product.name.toLowerCase()} distributor`,
    "industrial chemicals",
    "Ceekay Enterprise",
  ].join(", ");

  return (
    <div className="page active">
      <SEO
        title={productTitle}
        description={productDescription}
        keywords={seoKeywords}
        canonical={productUrl}
        ogImage={product.image ? `https://www.ceekayenterprise.com${product.image}` : undefined}
        ogType="product"
        schema={combinedSchema}
      />

      {/* Breadcrumb */}
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span> / </span>
        <Link to="/products">Products</Link>
        <span> / </span>
        <span>{product.name}</span>
      </nav>

      <div className="product-detail">
        <div className="product-detail-main">
          <div className="product-detail-image">
            {product.image && (
              <img
                src={product.image}
                alt={product.name}
                loading="lazy"
              />
            )}
          </div>

          <div className="product-detail-info">
            <h1>{product.name}</h1>
            <div className="product-price-large">{product.price}</div>
            <div className="product-description-full">
              <p>{product.description}</p>
            </div>

            {/* Product Specifications */}
            <div className="product-specs">
              <h3>Specifications</h3>
              <div className="specs-grid">
                <div className="spec-item">
                  <strong>Category:</strong> {product.category || "Industrial Chemicals"}
                </div>
                {formula && (
                  <div className="spec-item">
                    <strong>Formula:</strong> {formula}
                  </div>
                )}
                {casNumber && (
                  <div className="spec-item">
                    <strong>CAS Number:</strong> {casNumber}
                  </div>
                )}
                <div className="spec-item">
                  <strong>Packaging:</strong> Available in bulk quantities
                </div>
                <div className="spec-item">
                  <strong>Delivery:</strong> Across India
                </div>
              </div>
            </div>

            {/* Applications */}
            <div className="product-applications">
              <h3>Industrial Applications</h3>
              <ul>
                {product.keywords?.slice(0, 6).map((keyword, index) => (
                  <li key={index}>{keyword}</li>
                ))}
                <li>Various industrial processes</li>
                <li>Manufacturing applications</li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="product-cta-buttons">
              <button
                className="primary-cta"
                onClick={() => {
                  navigate("/contact", {
                    state: {
                      productName: product.name,
                      message: `I would like to inquire about ${product.name}. Please provide pricing, availability, and technical specifications.`,
                    },
                  });
                }}
              >
                Request Quote
              </button>
              <Link to="/products" className="secondary-cta">
                View All Products
              </Link>
            </div>
          </div>
        </div>

        {/* Safety & Handling */}
        <div className="product-safety">
          <h2>Safety & Handling Information</h2>
          <p>
            All our products are handled in accordance with industry safety
            standards. Material Safety Data Sheets (MSDS) are available upon
            request. Please ensure proper storage conditions and follow
            recommended handling procedures.
          </p>
          <p>
            For detailed safety information, technical specifications, and
            handling guidelines, please contact our technical support team.
          </p>
        </div>

        {/* Packaging & Availability */}
        <div className="product-packaging">
          <h2>Packaging & Availability</h2>
          <p>
            Available in various packaging options to suit your requirements.
            Bulk quantities available. Custom packaging solutions can be arranged
            for large orders.
          </p>
          <p>
            For information about minimum order quantities, pricing, and delivery
            timelines, please contact us with your specific requirements.
          </p>
        </div>

        {/* Related Products */}
        <div className="related-products">
          <h2>Other Products You Might Like</h2>
          <div className="related-products-grid">
            {productsData
              .filter((p) => p.id !== product.id && p.category === product.category)
              .slice(0, 3)
              .map((relatedProduct) => {
                const relatedSlug = relatedProduct.name
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "");
                return (
                  <Link
                    key={relatedProduct.id}
                    to={`/products/${relatedSlug}`}
                    className="related-product-card"
                  >
                    {relatedProduct.image && (
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                      />
                    )}
                    <h4>{relatedProduct.name}</h4>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;

